import { useMemo } from 'react';
import { Controller, type Control, type UseFormRegister, type UseFormWatch } from 'react-hook-form';
import type { Organization } from '@court-sync/shared';
import { AcademySettingsSection } from '@/components/academy/AcademySettingsSection';
import {
  WebsiteEmbedLinkOutFields,
  WebsiteEmbedWidgetsGrid,
} from '@/components/organization/WebsiteEmbedWidgets';
import { Field, FieldDescription, FieldError, FieldLabel, Switch, Textarea } from '@/components/ui';
import { useOrgWebsiteEmbedWidgets } from '@/hooks/useOrgWebsiteEmbedWidgets';
import { buildOrgAuthLinkOutUrl, isOrgBrandedPublicSiteActive } from '@/lib/orgPaths';
import { cn } from '@/lib/utils';

export interface OrgPublicSiteFormValues {
  slug: string;
  websiteEmbedEnabled: boolean;
  embedAllowedOriginsText: string;
}

export interface OrgPublicSiteSettingsFormValues extends OrgPublicSiteFormValues {
  primaryColor: string;
  secondaryColor: string;
  logoUrl: string;
  bannerUrl: string;
  facebook: string;
  instagram: string;
  twitter: string;
  linkedin: string;
  showCoachesOnPublicPage: boolean;
}

interface OrgWebsiteEmbedSettingsSectionProps {
  organization: Organization;
  register: UseFormRegister<OrgPublicSiteSettingsFormValues>;
  control: Control<OrgPublicSiteSettingsFormValues>;
  watch: UseFormWatch<OrgPublicSiteSettingsFormValues>;
  embedOriginsError?: string;
}

export function OrgWebsiteEmbedSettingsSection({
  organization,
  register,
  control,
  watch,
  embedOriginsError,
}: OrgWebsiteEmbedSettingsSectionProps) {
  const enabled = watch('websiteEmbedEnabled') ?? false;
  const slug = watch('slug') || organization.slug;

  const publicOrg = useMemo(
    () => ({
      slug,
      subdomain: organization.subdomain,
      subdomainEnabled: organization.subdomainEnabled,
    }),
    [slug, organization.subdomain, organization.subdomainEnabled]
  );

  const { academyWidgets, clubWidgets, allWidgets, brandedContext, isLoading } =
    useOrgWebsiteEmbedWidgets(publicOrg);
  const brandedActive = isOrgBrandedPublicSiteActive(publicOrg, brandedContext);
  const loginLinkOut = buildOrgAuthLinkOutUrl(publicOrg, '/login');
  const registerLinkOut = buildOrgAuthLinkOutUrl(publicOrg, '/register');

  return (
    <AcademySettingsSection
      title="Website embed"
      description={
        brandedActive
          ? 'Embed booking widgets on your own website using your branded subdomain URLs. Each widget is scoped to a single feature — not the full public home page.'
          : 'Embed booking widgets on your own website. Each widget is scoped to a single feature — not the full public home page.'
      }
    >
      <div className="space-y-6">
        <Controller
          name="websiteEmbedEnabled"
          control={control}
          render={({ field }) => (
            <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              <div>
                <FieldLabel htmlFor="org-website-embed-enabled">Enable website embed</FieldLabel>
                <FieldDescription id="org-embed-help">
                  Allows iframe widgets when the parent page origin is on your allowlist.
                </FieldDescription>
              </div>
              <Switch
                id="org-website-embed-enabled"
                checked={field.value ?? false}
                onCheckedChange={field.onChange}
                aria-describedby="org-embed-help"
              />
            </div>
          )}
        />

        <fieldset
          disabled={!enabled}
          className={cn('m-0 min-w-0 space-y-6 border-0 p-0', !enabled && 'opacity-60')}
        >
          <Field data-invalid={!!embedOriginsError}>
            <FieldLabel htmlFor="org-embed-origins">Allowed origins (one per line)</FieldLabel>
            <Textarea
              id="org-embed-origins"
              rows={4}
              placeholder="https://www.example.com"
              aria-invalid={!!embedOriginsError}
              {...register('embedAllowedOriginsText')}
            />
            <FieldDescription>HTTPS origins only. No wildcards.</FieldDescription>
            {embedOriginsError ? <FieldError>{embedOriginsError}</FieldError> : null}
          </Field>

          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading available widgets…</p>
          ) : (
            <div className="space-y-5">
              {academyWidgets.length > 0 ? (
                <section
                  className="space-y-3 rounded-xl border border-border bg-muted/10 p-4 sm:p-5"
                  aria-labelledby="embed-academy-heading"
                >
                  <div>
                    <h3 id="embed-academy-heading" className="text-sm font-semibold text-foreground">
                      Academy widgets
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Embed lesson browsing on your own site.
                    </p>
                  </div>
                  <WebsiteEmbedWidgetsGrid widgets={academyWidgets} disabled={!enabled} />
                </section>
              ) : null}

              {clubWidgets.length > 0 ? (
                <section
                  className="space-y-3 rounded-xl border border-border bg-muted/10 p-4 sm:p-5"
                  aria-labelledby="embed-club-heading"
                >
                  <div>
                    <h3 id="embed-club-heading" className="text-sm font-semibold text-foreground">
                      Club widgets
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Court hire, memberships, and play events when included on your club plan.
                    </p>
                  </div>
                  <WebsiteEmbedWidgetsGrid widgets={clubWidgets} disabled={!enabled} />
                </section>
              ) : null}

              {allWidgets.length === 0 ? (
                <WebsiteEmbedWidgetsGrid widgets={[]} disabled={!enabled} />
              ) : null}
            </div>
          )}

          {allWidgets.length > 0 ? (
            <WebsiteEmbedLinkOutFields
              loginUrl={loginLinkOut}
              registerUrl={registerLinkOut}
              disabled={!enabled}
            />
          ) : null}
        </fieldset>
      </div>
    </AcademySettingsSection>
  );
}
